import mockData from './mockData.json';

const DATABASE_KEY = 'order_module_local_database';
const SESSION_KEY = 'order_module_auth_session';

const clone = (value) => JSON.parse(JSON.stringify(value));

const nowIso = () => new Date().toISOString();

const normalizeArray = (value) => (Array.isArray(value) ? value : []);

const migrateDatabase = (database = {}) => {
  const seed = clone(mockData);
  const migratedDatabase = clone(database);
  const currentVersion = Number(database?.version || 1);
  const targetVersion = Number(seed.version || currentVersion);

  if (currentVersion < 2) {
    const seedUsersById = new Map(seed.users.map((user) => [user.id, user]));

    migratedDatabase.users = normalizeArray(migratedDatabase.users).map((user) => {
      const seedUser = seedUsersById.get(user.id);
      if (!seedUser) {
        return user;
      }

      return {
        ...user,
        username: seedUser.username,
        password: seedUser.password,
        full_name: seedUser.full_name,
        email: seedUser.email,
        role: seedUser.role,
        is_active: seedUser.is_active
      };
    });
  }

  migratedDatabase.version = targetVersion;
  return migratedDatabase;
};

const normalizeDatabase = (database = {}) => {
  const seed = clone(mockData);
  const migratedDatabase = migrateDatabase(database);

  return {
    version: migratedDatabase?.version ?? seed.version,
    metadata: {
      ...seed.metadata,
      ...(migratedDatabase?.metadata || {}),
      lastUpdatedAt: migratedDatabase?.metadata?.lastUpdatedAt || seed.metadata.lastUpdatedAt
    },
    users: normalizeArray(migratedDatabase?.users ?? seed.users),
    products: normalizeArray(migratedDatabase?.products ?? seed.products),
    productCartFlags: normalizeArray(migratedDatabase?.productCartFlags ?? seed.productCartFlags),
    orders: normalizeArray(migratedDatabase?.orders ?? seed.orders),
    generatedFiles: normalizeArray(migratedDatabase?.generatedFiles ?? seed.generatedFiles),
    cleanupHistory: normalizeArray(migratedDatabase?.cleanupHistory ?? seed.cleanupHistory)
  };
};

const canUseStorage = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const persistDatabase = (database) => {
  if (!canUseStorage()) {
    return;
  }

  const payload = {
    ...database,
    metadata: {
      ...database.metadata,
      lastUpdatedAt: nowIso()
    }
  };

  window.localStorage.setItem(DATABASE_KEY, JSON.stringify(payload));
};

export const getDatabase = () => {
  const fallback = normalizeDatabase(mockData);

  if (!canUseStorage()) {
    return fallback;
  }

  const rawDatabase = window.localStorage.getItem(DATABASE_KEY);
  if (!rawDatabase) {
    persistDatabase(fallback);
    return fallback;
  }

  try {
    return normalizeDatabase(JSON.parse(rawDatabase));
  } catch (error) {
    persistDatabase(fallback);
    return fallback;
  }
};

export const updateDatabase = (updater) => {
  const currentDatabase = getDatabase();
  const draft = clone(currentDatabase);
  const result = updater(draft);
  persistDatabase(draft);
  return result ?? draft;
};

export const resetDatabase = () => {
  const database = normalizeDatabase(mockData);
  persistDatabase(database);
  return database;
};

export const generateId = (prefix) => {
  const random = Math.random().toString(36).slice(2, 10);
  return `${prefix}_${Date.now()}_${random}`;
};

export const getSession = () => {
  if (!canUseStorage()) {
    return null;
  }

  const rawSession = window.localStorage.getItem(SESSION_KEY);
  if (!rawSession) {
    return null;
  }

  try {
    return JSON.parse(rawSession);
  } catch (error) {
    window.localStorage.removeItem(SESSION_KEY);
    return null;
  }
};

export const setSession = (userId) => {
  if (!canUseStorage()) {
    return null;
  }

  const session = {
    userId,
    createdAt: nowIso()
  };

  window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  return session;
};

export const clearSession = () => {
  if (!canUseStorage()) {
    return;
  }

  window.localStorage.removeItem(SESSION_KEY);
};

export const getCurrentUserRecord = () => {
  const session = getSession();
  if (!session?.userId) {
    return null;
  }

  const database = getDatabase();
  return database.users.find((user) => user.id === session.userId) || null;
};

export const sanitizeUser = (user) => {
  if (!user) {
    return null;
  }

  const { password, ...safeUser } = user;
  return safeUser;
};

export const buildAuthUser = (user) => {
  if (!user) {
    return null;
  }

  return {
    id: user.id,
    username: user.username,
    email: user.email,
    role: user.role
  };
};
